import React from "react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
    PieChart, Pie, Cell,
} from "recharts";
import { Sparkles } from "lucide-react";
import FunderShell from "./FunderShell";
import { SectionCard, MetricCard, StageLabel, scoreColor } from "./FunderUI";
import { getPortfolio, getProposals } from "../../service/FunderPortalService";

export default function FunderPortfolio() {
    return (
        <FunderShell activePage="Portfolio Overview">
            {(callId, call) => <Inner callId={callId} call={call} />}
        </FunderShell>
    );
}

const PIE_COLORS = ["var(--series-brand)", "#5b8fa8"];
const RADIAN = Math.PI / 180;

// Percentage labels drawn INSIDE each slice (in white). Outside labels get
// clipped by the SVG edge on narrow phone screens; slice names stay in the legend.
function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, value }) {
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#fff" fontSize={13} fontWeight={700} textAnchor="middle" dominantBaseline="central">
            {value}%
        </text>
    );
}

function Inner({ callId }) {
    const portfolio = getPortfolio(callId);
    const proposals = getProposals(callId);

    if (!portfolio || proposals.length === 0) {
        return <div className="funder-empty">No portfolio data available for this call.</div>;
    }

    const avg = (key) => Math.round(proposals.reduce((s, p) => s + Number(p[key]), 0) / proposals.length);
    const tripleHigh = proposals.filter((p) => p.novelty >= 80 && p.methodology >= 80 && p.impact >= 80).length;
    const frontierCount = proposals.filter((p) => p.frontier).length;

    const benchData = portfolio.benchmark.map((b) => ({ name: b.label, Portfolio: b.portfolio, Frontier: b.landscape }));
    const pieData = [
        { name: "Published", value: portfolio.composition.published },
        { name: "Preprint", value: portfolio.composition.preprint },
    ];

    const colored = (key, v) => <span style={{ color: scoreColor(key) }}>{v}</span>;

    return (
        <div className="funder-section-stack">
            {/* Header */}
            <div>
                <StageLabel>Portfolio · score signature</StageLabel>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-900)", margin: "6px 0 4px" }}>Portfolio Overview</h1>
                <p style={{ fontSize: 13.5, color: "var(--ink-450)", margin: 0 }}>
                    Score signature of this round's submissions against the research frontier.
                </p>
            </div>

            {/* KPI cards */}
            <div className="funder-metric-grid">
                <MetricCard label="Avg Novelty" value={colored("novelty", avg("novelty"))} sub="Mean across the pool" />
                <MetricCard label="Avg Methods" value={colored("methodology", avg("methodology"))} sub="Mean across the pool" />
                <MetricCard label="Avg Impact" value={colored("impact", avg("impact"))} sub="Mean across the pool" />
                <MetricCard label="Triple-high" value={tripleHigh} sub="All three scores ≥ 80" />
                <MetricCard label="Frontier-tagged" value={frontierCount} sub="Top novelty + impact signals" />
            </div>

            {/* Score signature vs frontier */}
            <SectionCard kicker="Score signature" title="Portfolio vs the research frontier"
                subtitle="How this round's submissions score relative to the broader funded landscape.">
                <div style={{ marginBottom: 16 }}>
                    {portfolio.benchmark.map((b) => (
                        <div key={b.dim} style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-900)", marginBottom: 4 }}>{b.label}</div>
                            <div className="funder-bar-row">
                                <span style={{ fontSize: 11.5, color: "var(--ink-450)" }}>Portfolio</span>
                                <div className="funder-bar-track"><div className="funder-bar-fill" style={{ width: `${b.portfolio}%`, background: scoreColor(b.dim) }} /></div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(b.dim) }}>{b.portfolio}</span>
                            </div>
                            <div className="funder-bar-row">
                                <span style={{ fontSize: 11.5, color: "var(--ink-350)" }}>Frontier</span>
                                <div className="funder-bar-track"><div className="funder-bar-fill" style={{ width: `${b.landscape}%`, background: "var(--chart-muted)" }} /></div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-350)" }}>{b.landscape}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                        <BarChart data={benchData} margin={{ top: 10, right: 16, bottom: 4, left: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-400)" }} tickLine={false} axisLine={{ stroke: "var(--border)" }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "var(--ink-400)" }} tickLine={false} axisLine={false} width={28} />
                            <Tooltip cursor={{ fill: "var(--surface-2)" }} contentStyle={{ fontSize: 12, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="Portfolio" fill="var(--brand-border)" radius={[6, 6, 0, 0]} maxBarSize={48} />
                            <Bar dataKey="Frontier" fill="var(--chart-muted)" radius={[6, 6, 0, 0]} maxBarSize={48} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </SectionCard>

            {/* Composition + insight */}
            <div className="funder-grid-2">
                <SectionCard kicker="Composition" title="Published vs preprint outputs">
                    <div style={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="72%" labelLine={false} label={renderPieLabel}>
                                    {pieData.map((d, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }} formatter={(v) => `${v}%`} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </SectionCard>

                <SectionCard kicker="Top strategic insight" title="What the score signature says">
                    <p style={{ fontSize: 13.5, color: "var(--ink-700)", lineHeight: 1.55, marginTop: 0 }}>{portfolio.insight}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {portfolio.tags.map((t) => (
                            <span key={t} className="funder-tag funder-tag-active" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                <Sparkles size={12} /> {t}
                            </span>
                        ))}
                    </div>
                </SectionCard>
            </div>

            {/* Archetypes */}
            <SectionCard kicker="Score archetypes" title="How the pool breaks down">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                    {portfolio.archetypes.map((a) => (
                        <div key={a.name} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 14 }}>
                            <div style={{ fontSize: 28, fontWeight: 700, color: "var(--brand-text)", lineHeight: 1 }}>{a.count}</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-900)", marginTop: 6 }}>{a.name}</div>
                            <div style={{ fontSize: 11.5, color: "var(--ink-400)", marginTop: 2 }}>{a.desc}</div>
                        </div>
                    ))}
                </div>
            </SectionCard>
        </div>
    );
}
